import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormError,
  Input,
  Label,
  Textarea,
} from "@simplicity/ui";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { apiFetch } from "../../api";
import type { ApiKeyWithToken, Project } from "../../configTypes";
import { Breadcrumbs } from "./Breadcrumbs";

export function GenerateApiKey() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId") ?? "";

  const [projectName, setProjectName] = useState<string>("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedKey, setGeneratedKey] = useState<ApiKeyWithToken | null>(null);

  useEffect(() => {
    if (!projectId) return;
    void apiFetch<Project>(`/api/projects/${projectId}`).then((p) => setProjectName(p.name));
  }, [projectId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const key = await apiFetch<ApiKeyWithToken>(`/api/projects/${projectId}/api-keys`, {
        method: "POST",
        body: JSON.stringify({ name: name.trim() }),
      });
      setGeneratedKey(key);
      await navigator.clipboard.writeText(key.token);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate API key");
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <Breadcrumbs
        items={[
          { label: "Projects", to: "/config/projects" },
          { label: projectName || "...", to: `/config/projects/${projectId}` },
          { label: "Generate API Key" },
        ]}
      />
      <h2 className="mb-6 text-2xl text-foreground">Generate API Key</h2>
      <div className="form-card max-w-[540px]">
        <form onSubmit={(e) => void handleSubmit(e)}>
          <div className="mb-4">
            <Label htmlFor="key-name" className="form-label">
              Name
            </Label>
            <Input
              id="key-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter name for key (e.g. Production, Staging)"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- inside <Dialog>; focus name field on open
              autoFocus
              autoComplete="one-time-code"
              disabled={!!generatedKey}
            />
          </div>
          {error && <FormError assertive>{error}</FormError>}
          {!generatedKey && (
            <div className="form-actions">
              <Button type="button" variant="outline" onClick={() => void navigate(-1)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !name.trim()}>
                {submitting ? "Generating..." : "Generate"}
              </Button>
            </div>
          )}
        </form>
      </div>

      <Dialog
        open={!!generatedKey}
        onOpenChange={() => void navigate(`/config/projects/${projectId}`)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>API key created</DialogTitle>
            <DialogDescription>
              Copy this token now. It cannot be retrieved after closing this dialog.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            className="min-h-[80px] font-mono text-xs"
            readOnly
            value={generatedKey?.token ?? ""}
            onFocus={(e) => e.target.select()}
          />
          <p className="text-xs text-muted-foreground">
            Key &ldquo;{generatedKey?.name}&rdquo; has been copied to your clipboard.
          </p>
          <DialogFooter>
            <Button onClick={() => void navigate(`/config/projects/${projectId}`)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
